import { Link } from 'react-router-dom';
import { useRef } from 'react';
import manifest from '@curriculum/curriculum_manifest.json';
import { SEO } from '@/components/SEO';
import { PathNavigator } from './landing/PathNavigator';
import { FeatureCards } from './landing/FeatureCards';
import { SchoolComparison } from './landing/SchoolComparison';
import { FAQ } from './landing/FAQ';
import { KoshyThomasTrust } from './landing/KoshyThomasTrust';
import { useCursorParallax } from '@/lib/useCursorParallax';
import { useScrollReveal } from '@/lib/useScrollReveal';
import { JournalLedgerSimulation } from '@sim/journal-ledger/JournalLedgerSimulation';
import { useStudentProgress } from '@/hooks/useStudentProgress';
import popularChapters from '@/content/popular-chapters.data.json';
import { excelCourse, excelClasses, classPath } from '@/content/excelCourse';

// Pre-compute chapter count once — sum across ALL boards (CBSE + Kerala = 137)
const totalChapters = manifest.boards.reduce(
  (total, b) =>
    total +
    b.grades.reduce(
      (n, g) => n + g.subjects.reduce((m, s) => m + s.units.reduce((k, u) => k + u.chapters.length, 0), 0),
      0,
    ),
  0,
);

export function HomePage() {
  // ── Cursor parallax refs ──────────────────────────────────────────────────
  const heroRef      = useRef<HTMLElement>(null);
  const badgeRef     = useRef<HTMLDivElement>(null);
  const headlineRef  = useRef<HTMLHeadingElement>(null);
  const statsRef     = useRef<HTMLDivElement>(null);

  useCursorParallax({
    containerRef: heroRef,
    layers: [
      { ref: headlineRef, depth: -0.4, maxTravel: 8 },
      { ref: badgeRef,    depth:  0.7, maxTravel: 10 },
      { ref: statsRef,    depth:  0.5, maxTravel: 8  },
    ],
  });

  const { lastVisited } = useStudentProgress();

  // ── Scroll-reveal refs for section headings ───────────────────────────────
  const navigatorHeadRef    = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const featuresHeadRef     = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const simulatorHeadRef    = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const simulatorEmbedRef   = useScrollReveal<HTMLDivElement>({ delay: 120 });
  const comparisonHeadRef   = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const faqHeadRef          = useScrollReveal<HTMLDivElement>({ threshold: 0.1 });
  const ctaRef              = useScrollReveal<HTMLDivElement>({ threshold: 0.08 });

  return (
    <div>
      <SEO
        title="ACES — Class 11 & 12 Commerce Notes, Sims & Quizzes (CBSE & Kerala)"
        description="Free notes, interactive simulations, video lessons and quizzes for Plus One & Plus Two (Class 11 & 12) Accountancy, Business Studies and Economics — CBSE & Kerala syllabus. No login, no fee."
        canonical="https://acescommerce.com/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              name: 'ACES — Plus One & Plus Two Commerce Notes',
              alternateName: 'ACES',
              url: 'https://acescommerce.com/',
              inLanguage: 'en-IN',
              description: 'Interactive learning for Class 11 & 12 Accountancy, Business Studies, and Economics. CBSE & Kerala HSE.',
            },
            {
              '@type': 'EducationalOrganization',
              name: 'ACES',
              alternateName: 'ACES Commerce',
              url: 'https://acescommerce.com/',
              description: 'Independent educational platform for Class 11 & 12 (Plus One & Plus Two) commerce — Accountancy, Business Studies and Economics — aligned to CBSE NCERT and Kerala SCERT syllabi.',
              sameAs: ['https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw'],
              founder: { '@type': 'Person', name: 'Koshy Thomas', jobTitle: 'Commerce teacher' },
            },
          ],
        }}
      />

      {/* ===================================================================
          HERO — flat khadi-paper field with a faint indigo jaali weave
      =================================================================== */}
      <section
        ref={heroRef}
        className="relative isolate overflow-hidden bg-paper"
      >
        {/* Soft saffron dawn glow behind the headline (decorative) */}
        <div
          aria-hidden="true"
          className="absolute -top-48 left-1/2 -z-10 h-[38rem] w-[38rem] -translate-x-1/2 rounded-full pointer-events-none bg-[radial-gradient(closest-side,rgba(224,123,26,0.12),transparent)]"
        />
        {/* Jaali cross-hatch — faint indigo weave, masked to fade at edges */}
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(47,58,143,0.05) 0, rgba(47,58,143,0.05) 1px, transparent 1px, transparent 14px), repeating-linear-gradient(-45deg, rgba(47,58,143,0.05) 0, rgba(47,58,143,0.05) 1px, transparent 1px, transparent 14px)',
            WebkitMaskImage:
              'radial-gradient(ellipse 70% 60% at 50% 42%, #000 35%, transparent 100%)',
            maskImage:
              'radial-gradient(ellipse 70% 60% at 50% 42%, #000 35%, transparent 100%)',
          }}
        />

        <div className="max-w-5xl mx-auto px-4 pt-20 pb-14 text-center">
          {/* Teacher proof — real-person credibility, above the fold */}
          <div
            ref={badgeRef}
            className="inline-block animate-fade-in motion-reduce:animate-none"
            style={{ animationDelay: '80ms' }}
          >
            <a
              href="https://www.youtube.com/channel/UCh4KBkzwSbk-TEWZTi1maxw"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 bg-cream/90 backdrop-blur border border-ink-200/80 pl-1.5 pr-4 py-1.5 rounded-full mb-8 shadow-soft hover:border-accent-500/60 hover:shadow-card transition-[border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2"
              aria-label="Taught by Koshy Thomas — 14,000+ subscribers on YouTube, opens in new tab"
            >
              <img
                src="/brand/koshy-thomas.webp"
                alt=""
                width={28}
                height={28}
                loading="eager"
                decoding="async"
                className="w-7 h-7 rounded-full object-cover object-top ring-1 ring-accent-500"
              />
              <span className="text-[12px] text-ink-700">
                Taught by <span className="font-semibold text-ink-900">Koshy Thomas</span>
                <span className="text-ink-300 mx-1.5" aria-hidden="true">·</span>
                <span className="font-semibold text-accent-600">14,000+ on YouTube</span>
              </span>
            </a>
          </div>

          {/* Headline — outcome-led, keyword-rich, one saffron-italic accent */}
          <h1
            ref={headlineRef}
            className="font-serif text-[2.6rem] sm:text-[3.4rem] lg:text-[4.1rem] text-ink-900 tracking-tight leading-[1.06] max-w-3xl mx-auto text-balance"
            style={{ fontVariationSettings: '"wght" 700' }}
          >
            <em className="italic text-accent-600">Understand</em>{' '}
            Plus One &amp; Plus Two commerce{' '}
            <span className="text-ink-500">— not just memorise it.</span>
          </h1>

          <p className="mt-6 text-[1.08rem] sm:text-lg text-ink-600 max-w-xl mx-auto leading-relaxed text-pretty">
            Free notes, videos, simulations &amp; quizzes for Accountancy, Business Studies and
            Economics — CBSE and Kerala syllabus. No login. No fee.
          </p>

          {/* CTA — one primary action, one quiet alternative */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
            <a
              href="#navigator"
              className="aces-btn-primary px-7 py-3.5"
            >
              Start learning →
            </a>
            <a
              href="#demo"
              className="text-sm font-medium text-ink-600 hover:text-accent-600 underline-offset-4 hover:underline transition-colors duration-150"
            >
              or see how it works ↓
            </a>
          </div>

          {/* Stats */}
          <div
            ref={statsRef}
            className="mt-10 inline-flex items-center justify-center gap-3 sm:gap-6 rounded-full border border-ink-200/80 bg-cream/70 backdrop-blur px-4 sm:px-6 py-2.5 text-xs text-ink-500 shadow-soft"
          >
            <StatPill n={totalChapters} label="chapters" />
            <span className="w-px h-4 bg-ink-200" aria-hidden="true" />
            <StatPill n={2} label="boards" />
            <span className="w-px h-4 bg-ink-200" aria-hidden="true" />
            <StatPill n={3} label="subjects" />
          </div>
        </div>

        {/* Wave transition into next section */}
        <div className="relative h-10 -mb-1" aria-hidden="true">
          <svg viewBox="0 0 1440 40" preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
            <path
              d="M0,40 C240,10 480,0 720,14 C960,28 1200,6 1440,40 L1440,40 L0,40 Z"
              className="fill-cream"
            />
          </svg>
        </div>
      </section>

      {/* ===================================================================
          KOSHY THOMAS TRUST BLOCK — right after hero, before navigator
          Establishes credibility immediately: "who made this?"
      =================================================================== */}
      <KoshyThomasTrust />

      {/* ===================================================================
          ACCOUNTING CYCLE DEMO — interactive hook, surfaced high so cold
          visitors can touch the product before committing
      =================================================================== */}
      <section id="demo" className="bg-cream border-b border-ink-200 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-20">
          <div ref={simulatorHeadRef} className="text-center max-w-xl mx-auto mb-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" aria-hidden="true" />
              Try it now — no login needed
            </span>
            <h2 className="font-serif text-3xl md:text-[2.4rem] text-ink-900 tracking-tight leading-tight">
              See how a transaction becomes a trial balance.
            </h2>
            <p className="text-ink-500 mt-2.5 text-sm leading-relaxed">
              Walk through a real accounting cycle — Transactions → Journal → Ledger → Trial Balance — in under two minutes.
            </p>
          </div>
          <div ref={simulatorEmbedRef}>
            <JournalLedgerSimulation embedded />
          </div>
        </div>
      </section>

      {/* ===================================================================
          PATH NAVIGATOR — the primary action
      =================================================================== */}
      <section id="navigator" className="bg-cream">
        <div className="max-w-3xl mx-auto px-4 pt-10 pb-14">
          {/* ── Continue where you left off ── */}
          {lastVisited && (
            <div className="mb-8">
              <Link
                to={`/${lastVisited.board}/${lastVisited.grade}/${lastVisited.subject}/${lastVisited.slug}/`}
                className="group flex items-center gap-4 rounded-card-lg border border-accent-200 bg-gradient-to-r from-accent-100/60 to-accent-50 px-4 sm:px-5 py-4 shadow-soft hover:border-accent-500/60 hover:shadow-card transition-[border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-600 focus-visible:ring-offset-2"
                aria-label={`Continue reading ${lastVisited.name}`}
              >
                <span
                  aria-hidden="true"
                  className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-ink-900 text-base group-hover:bg-accent-600 group-hover:text-white transition-colors duration-150 motion-reduce:transition-none"
                >
                  ▶
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.1em] text-accent-600 mb-0.5">
                    Continue where you left off
                  </div>
                  <div className="text-sm font-medium text-ink-900 truncate">
                    {lastVisited.name}
                  </div>
                </div>
                <span className="text-ink-400 text-sm group-hover:text-accent-600 transition-colors duration-150">→</span>
              </Link>
            </div>
          )}

          <div ref={navigatorHeadRef} className="text-center mb-7">
            <h2 className="font-serif text-2xl md:text-3xl text-ink-900 tracking-tight">
              Where do you want to start?
            </h2>
            <p className="text-ink-500 text-sm mt-2">
              Pick your board, class and subject — your chapter list is one click away.
            </p>
          </div>

          <PathNavigator />

          {/* Quick-jump belt */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs text-ink-500">
            <span className="mr-1 text-ink-500">Jump to:</span>
            {manifest.boards.flatMap((b) =>
              b.grades.flatMap((g) =>
                g.subjects.map((s) => (
                  <Link
                    key={`${b.id}-${g.id}-${s.id}`}
                    to={`/${b.id}/${g.id}/${s.id}/`}
                    className="px-3 py-1.5 rounded-full border border-ink-200/80 bg-cream text-ink-600 shadow-soft hover:border-accent-500/60 hover:text-accent-700 hover:bg-accent-50 transition-colors duration-150"
                  >
                    {b.id === 'cbse' ? 'CBSE' : 'Kerala'} {g.id === '11' ? '+1' : '+2'} · {s.name}
                  </Link>
                )),
              ),
            )}
          </div>

          {/* Popular chapters, from the search data */}
          <div className="mt-12">
            <h3 className="font-serif text-2xl text-ink-900 text-center tracking-tight">Popular chapters</h3>
            <p className="text-ink-500 text-sm text-center mt-2">The chapters that appear most often in search results.</p>
            <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {popularChapters.homepage.map((c) => (
                <li key={c.path}>
                  <Link to={c.path} className="aces-card-hover block h-full rounded-xl border border-ink-200/80 bg-cream px-4 py-3.5 shadow-soft hover:border-accent-500/50">
                    <span className="block text-sm font-medium text-ink-900">{c.name}</span>
                    <span className="block text-xs text-ink-500 mt-0.5">{c.context}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===================================================================
          FREE EXCEL COURSE — short vertical classes, links to /excel-course
      =================================================================== */}
      <section className="bg-cream border-t border-ink-200">
        <div className="max-w-6xl mx-auto px-4 py-14 md:py-16 grid gap-8 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
          <div className="max-w-xl">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              New · Free Excel course
            </span>
            <h2 className="font-serif text-3xl md:text-[2.3rem] text-ink-900 tracking-tight leading-tight">
              Excel Study: basics to pro, in short classes
            </h2>
            <p className="text-ink-600 mt-3 text-sm leading-relaxed">
              {excelCourse.planned} classes of 1–2.5 minutes each, {excelCourse.made} ready now. Every result is
              checked in real Microsoft Excel, and each class has an AI prompt you can try.
            </p>
            <Link to="/excel-course/" className="aces-btn-primary mt-6">Open the Excel course →</Link>
          </div>
          <div className="flex gap-3">
            {excelClasses.slice(0, 3).map((c) => (
              <Link
                key={c.class}
                to={classPath(c.class)}
                className="block w-[min(28vw,128px)] rounded-card overflow-hidden border border-ink-200/80 shadow-card aces-card-hover"
                aria-label={`Excel Class ${c.class}: ${c.topic}`}
              >
                <img src={c.thumbnail} alt="" width={540} height={960} loading="lazy" decoding="async" className="block w-full aspect-[9/16] object-cover" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          WHAT YOU GET — four-up feature cards on a tinted band
      =================================================================== */}
      <section className="bg-paper border-y border-ink-200">
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-20">
          <div ref={featuresHeadRef} className="text-center max-w-xl mx-auto mb-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-accent-600 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              What's inside
            </span>
            <h2 className="font-serif text-3xl md:text-[2.4rem] text-ink-900 tracking-tight leading-tight">
              What you get in every chapter
            </h2>
            <p className="text-ink-500 mt-2.5 text-sm leading-relaxed">
              Beyond reading — the tools that make concepts stick.
            </p>
          </div>
          <FeatureCards />
        </div>
      </section>

      {/* ===================================================================
          SCHOOL COMPARISON
      =================================================================== */}
      <section className="bg-paper border-b border-ink-200">
        <div className="max-w-5xl mx-auto px-4 py-16 md:py-20">
          <div ref={comparisonHeadRef} className="text-center max-w-2xl mx-auto mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.14em] text-accent-600 mb-3">
              How it fits
            </span>
            <h2 className="font-serif text-3xl md:text-[2.4rem] text-ink-900 tracking-tight leading-tight">
              Use ACES <em className="italic text-accent-600">with</em> school, not instead of it
            </h2>
            <p className="text-ink-600 mt-2.5 text-sm leading-relaxed">
              Your teacher does what we can't. We do what they can't — replay any concept until it clicks.
            </p>
          </div>
          <SchoolComparison />
        </div>
      </section>

      {/* ===================================================================
          FAQ — alternating band
      =================================================================== */}
      <section className="bg-cream border-y border-ink-200">
        <div className="max-w-3xl mx-auto px-4 py-16 md:py-20">
          <div ref={faqHeadRef} className="text-center max-w-xl mx-auto mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.14em] text-accent-600 mb-3">
              FAQ
            </span>
            <h2 className="font-serif text-3xl md:text-[2.4rem] text-ink-900 tracking-tight">Common questions</h2>
          </div>
          <FAQ />
        </div>
      </section>

      {/* ===================================================================
          FINAL CTA — deep indigo band, saffron accents, jaali weave
      =================================================================== */}
      <section className="aces-band-night bg-night-700 relative overflow-hidden">
        {/* Jaali cross-hatch — saffron weave on indigo, masked to fade */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(45deg, rgba(224,123,26,0.10) 0, rgba(224,123,26,0.10) 1px, transparent 1px, transparent 16px), repeating-linear-gradient(-45deg, rgba(224,123,26,0.10) 0, rgba(224,123,26,0.10) 1px, transparent 1px, transparent 16px)',
            WebkitMaskImage:
              'radial-gradient(ellipse 75% 80% at 50% 50%, #000 30%, transparent 100%)',
            maskImage:
              'radial-gradient(ellipse 75% 80% at 50% 50%, #000 30%, transparent 100%)',
          }}
        />

        <div ref={ctaRef} className="relative max-w-3xl mx-auto px-4 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-accent-200 border border-accent-500/40 bg-accent-500/10 px-3.5 py-1.5 rounded-full mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
            Free forever · No account needed
          </div>
          <h2 className="font-serif text-3xl md:text-[2.6rem] text-white tracking-tight leading-tight">
            Ready? Open a chapter and see.
          </h2>
          <p className="text-ink-100 mt-4 text-sm max-w-md mx-auto leading-relaxed">
            No sign-up. No paywall. Just pick your path above, or jump straight in:
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/cbse/11/accountancy/"
              className="aces-btn-primary"
            >
              CBSE Class 11 Accountancy →
            </Link>
            <a
              href="#navigator"
              className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-ink-100 border border-white/20 bg-white/5 backdrop-blur hover:border-white/40 hover:bg-white/10 transition-colors duration-150"
            >
              Choose your board, class and subject →
            </a>
          </div>
          <p className="mt-9 text-xs text-ink-400">
            {totalChapters} chapters · 3 subjects · CBSE &amp; Kerala · Free forever
          </p>
        </div>
      </section>
    </div>
  );
}

function StatPill({ n, label }: { n: number; label: string }) {
  return (
    <span className="font-medium">
      <span className="font-serif text-base text-ink-700 tabular-nums">{n}</span>
      {' '}{label}
    </span>
  );
}
